import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/feature/PageSEO';
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { categories, type Category } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    navigate(`/category/${catSlug}`);
  };

  return (
    <>
      <PageSEO />
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{activeCategory.name}</span>
          </nav>

          <div className="mb-10">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {activeCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {activeCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10 pb-6 border-b border-background-200/70">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                      <span className="text-xs text-foreground-500">·</span>
                      <span className="text-xs text-foreground-500">{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}